import React, { Component, ReactNode } from 'react';
import Icon from '../../icons/Icon';
import FormicaSearchSelectBox, { FormicaSelectBoxItem } from '../custom/FormicaSearchSelectBox';
import { AnimatedButton } from '@trlogic/ui-pack';
import { FormicaProps, withFormica } from '../../helpers/withFormica';
import { RootState } from '../../redux/store';
import { clearPage } from '../../redux/actions/PageActions';
import { connect } from 'react-redux';
import { ConnectedProps } from 'react-redux/es/exports';
import { DashboardState } from '../../redux/reducers/DashboardReducer';
import { FormicaTimePickerDate } from '../custom/custom-time-picker/TimePickerDomain';
import TimePickerContainer, { saveDatesToLocalStorage } from '../custom/custom-time-picker/TimePickerContainer';
import { changeDashboard, setDashboardResponse } from '../../redux/actions/DashboardActions';
import MessageUtil from '../../util/MessageUtil';
import DashboardService from '../../service/DashboardService';
import ServiceContainer from '../../config/ServiceContainer';
import { ServiceNames } from '../../constant/ServiceNames';
import LanguageUtil from '../../util/LanguageUtil';
import { User } from '../../domain/rbac/RbacDomains';
import RoleHelper from '../../RoleHelper';

class DashboardInformationComponent extends Component<Props> {
  private dashboardService: DashboardService = ServiceContainer.getInstance().get(ServiceNames.DASHBOARD);
  private pendingStartDate: FormicaTimePickerDate | null = null;

  public render(): ReactNode {
    const {
      dashboard: { dashboard, filter },
    } = this.props;
    const disabled = !RoleHelper.canEdit('Dashboard');
    return (
      <div
        id='information-container'
        className={
          'flex select-none w-full px-5 py-5 font-custom justify-between gap-y-5 gap-x-5 lg:justify-start items-start lg:items-center  flex-col lg:flex-row font-formica'
        }
      >
        <div id='edit-section' className={'flex items-center justify-start w-1/2 gap-x-5'}>
          <div
            id='icon'
            className={`bg-secondary/80 rounded-full w-[60px] h-[60px] min-h-[60px] min-w-[60px] 2xl:w-[80px] 2xl:h-[80px] 2xl:min-h-[80px] 2xl:min-w-[80px] shrink-0 flex items-center justify-center`}
          >
            <div id='icon' className={'text-f-purple'}>
              <Icon icon={'dashboard'} className={'hidden w-[24px] h-[24px] 2xl:w-[34px] 2xl:h-[34px]'} />
            </div>
          </div>
          <div id={'editable-fields'} className={'flex flex-col w-full'}>
            <div className={'flex items-center w-full'}>
              <input
                value={LanguageUtil.get({ id: 'common.label.dashboard', defaultMessage: 'Dashboard' })}
                disabled
                className={
                  'w-full h-[40px] select-none text-3xl 2xl:text-4xl bg-transparent border-none shadow-none hover:border-none focus:outline-none text-f-black-2'
                }
              />
            </div>
            <div className={'flex items-center group relative'}>
              <textarea
                value={LanguageUtil.get({ id: 'common.label.dashboard', defaultMessage: 'Dashboard' })}
                disabled
                className={
                  'w-[98%] h-[30px] min-w-[300px] bg-white custom-editor-component-description hover:border-b-[1px] hover:border-f-purple flex items-center justify-start py-1.5 text-start text-md 2xl:text-lg focus:border-b-[1px] focus:border-f-purple resize-none focus:outline-none text-f-black-3'
                }
              />
            </div>
          </div>
        </div>
        <div className={'flex flex-row w-full justify-end gap-x-5'}>
          <div className={'flex w-full items-center gap-x-5'}>
            <FormicaSearchSelectBox
              rounded
              value={dashboard?.selectedTransaction}
              placeholder={LanguageUtil.get({ id: 'common.input.placeholder.channel', defaultMessage: 'Dashboard' })}
              options={this.getTransactions()}
              disabled={!RoleHelper.canView('Dashboard')}
              onChange={this.onChangeTransaction}
            />
            <TimePickerContainer
              startDate={filter.startDate}
              endDate={filter.endDate}
              onChangeStartDate={this.onChangeStartDate}
              onChangeEndDate={this.onChangeEndDate}
            />
          </div>
          <div className={'flex items-center gap-x-5'}>
            <AnimatedButton
              onClick={this.onClickAdd}
              icon={'add'}
              customType={'secondary'}
              customSize={'md'}
              disabled={disabled}
              text={LanguageUtil.get({ id: 'common.button.add', defaultMessage: 'Add' })}
            />
            <AnimatedButton
              onClick={this.onClickSave}
              icon={'save'}
              customType={'primary'}
              customSize={'md'}
              disabled={disabled}
              text={LanguageUtil.get({ id: 'common.button.save', defaultMessage: 'Save' })}
            />
          </div>
        </div>
      </div>
    );
  }

  private onClickSave = async () => {
    const {dashboard: { dashboard, filter }, changeDashboard} = this.props;
    const dashboardToSave = { ...dashboard, startDate: filter.startDate, endDate: filter.endDate };
    await this.dashboardService.save(dashboardToSave);
    changeDashboard(dashboardToSave);
  };
  private onClickAdd = () => {
    const {
      dashboard: { dashboard },
      router,
    } = this.props;
    if (!dashboard.name || dashboard.name == '') {
      MessageUtil.warning({
        content: LanguageUtil.get({ id: 'messages.warning.selectTransactionToAdd', defaultMessage: 'Please select a transaction to add.' }),
      });
    } else {
      router.navigate(`/dashboard/${dashboard.name}/create`);
    }
  };

  private onChangeTransaction = (item: FormicaSelectBoxItem) => {
    const {
      dashboard: { dashboard },
      changeDashboard,
      setDashboardResponse,
    } = this.props;
    changeDashboard({ ...dashboard, selectedTransaction: item.value });
    setDashboardResponse({
      charts: [],
      rulePerformance: [],
      name: dashboard.name,
    });
    this.props.resubscribeTracker();
  };

  private onChangeStartDate = (date: FormicaTimePickerDate) => {
    this.pendingStartDate = date;
  };
  private onChangeEndDate = (date: FormicaTimePickerDate) => {
    const {dashboard: { filter }, onDateRangeApplied} = this.props;
    const startDate = this.pendingStartDate ?? filter.startDate;
    this.pendingStartDate = null;
    saveDatesToLocalStorage(startDate, date);
    onDateRangeApplied(startDate, date);
  };

  private getTransactions = () => {
    const {
      dashboard: { transactions },
    } = this.props;
    return (
      transactions?.map((transaction) => ({
        label: transaction.description || transaction.name,
        value: transaction.name,
      })) || []
    );
  };
}

interface Props extends ReduxProps, FormicaProps {
  resubscribeTracker: VoidFunction;
  onDateRangeApplied: (startDate: FormicaTimePickerDate, endDate: FormicaTimePickerDate) => void;
}

const mapStateToProps = (state: RootState) => ({
  dashboard: state.dashboard as DashboardState,
  user: state.auth.user as User,
});
const mapDispatchToProps = {
  changeDashboard,
  clearPage,
  setDashboardResponse,
};

const connector = connect(mapStateToProps, mapDispatchToProps);
type ReduxProps = ConnectedProps<typeof connector>;

export default withFormica(connector(DashboardInformationComponent));
